/* Mutual Optical — Quick Order
 * 交互还原自老 BSS Customer Portal 的 Product List：
 *   逐行商品 + 搜索/品牌/集合筛选 + 排序 + 顶部 running cart；
 *   单变体行内填数量加购，多变体开弹窗逐配色填数量后批量加购。
 *
 * 价格只来自 BSS 改写后的锚点（见 snippets/mo-qo-price-anchor.liquid）。
 * 未登录时锚点里是 LTSP 的 "Login to see price"，因此**绝不**用 /products.json 里的
 * 基础价渲染任何金额 —— 那会向游客泄漏批发价。
 *
 * 库存只显示 In Stock / Out of Stock，不显示数字（老页面会显示 "In Stock: 196"，
 * 甚至出现过 "In Stock: -1"）。/products.json 也不返回库存数，技术上无从显示。
 */
(function () {
  'use strict';

  var root, cfg, bank;
  var Catalog = { list: [], byVariant: new Map(), brands: [], collections: [] };
  var view = { q: '', brand: '', collection: '', sort: 'title-asc', shown: 0, filtered: [] };
  var collectionIndex = new Map();     // handle -> Set(productId)
  var modal = { product: null, qty: new Map() };
  var busy = false;

  var $ = function (s, c) { return (c || root).querySelector(s); };
  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  var up = function (s) { return String(s || '').trim().toUpperCase(); };

  /* 登录后回到 Quick Order，别把人丢在账户首页 */
  function loginHref() {
    return cfg.loginUrl + '?return_url=' + encodeURIComponent(location.pathname);
  }

  function money(cents) {
    var v = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (cfg.moneyFormat || '${{amount}}').replace(/\{\{\s*\w+\s*\}\}/, v).replace(/<[^>]*>/g, '');
  }

  /* ───────── 目录 ───────── */
  var CACHE_KEY = 'mo-qo-catalog-v2';
  var CACHE_TTL = 10 * 60 * 1000;

  function index(products) {
    Catalog.list = products.map(function (p) {
      var img = (p.images && p.images[0] && p.images[0].src) || '';
      return {
        id: p.id, handle: p.handle, title: p.title, image: img,
        brand: p.vendor || '',
        optionName: (p.options && p.options[0] && p.options[0].name) || 'Colour',
        variants: (p.variants || []).map(function (v) {
          return {
            id: v.id, sku: v.sku || '',
            colour: v.option1 || v.title || '',
            available: v.available !== false,
            image: (v.featured_image && v.featured_image.src) || img,
          };
        }),
      };
    });
    Catalog.byVariant.clear();
    var brands = new Set();
    Catalog.list.forEach(function (p) {
      if (p.brand) brands.add(p.brand);
      p.variants.forEach(function (v) { Catalog.byVariant.set(String(v.id), { product: p, variant: v }); });
    });
    Catalog.brands = [].concat(Array.from(brands)).sort();
  }

  function loadCatalog() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var c = JSON.parse(raw);
        if (Date.now() - c.t < CACHE_TTL && c.p && c.p.length) { index(c.p); return Promise.resolve(); }
      }
    } catch (e) {}
    return fetch(cfg.catalogUrl, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        index(d.products || []);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), p: d.products })); } catch (e) {}
      });
  }

  function loadCollections() {
    return fetch(cfg.collectionsUrl, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        // all-products 与目录等价，放进筛选器只会让人误以为是子集
        Catalog.collections = (d.collections || []).filter(function (c) { return c.handle !== 'all-products'; });
      })
      .catch(function () { Catalog.collections = []; });
  }

  /* 集合→商品的映射不在 /products.json 里，选中某集合时按需拉一次并缓存 */
  function ensureCollection(handle) {
    if (!handle || collectionIndex.has(handle)) return Promise.resolve();
    return fetch('/collections/' + handle + '/products.json?limit=250', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        collectionIndex.set(handle, new Set((d.products || []).map(function (p) { return p.id; })));
      })
      .catch(function () { collectionIndex.set(handle, null); });
  }

  /* ───────── 价格银行（BSS 锚点） ───────── */
  var PriceBank = {
    cache: new Map(),

    harvest: function () {
      if (!bank) return;
      bank.querySelectorAll('[data-mo-qo-anchor]').forEach(function (el) {
        var id = el.getAttribute('data-variant-id');
        var c = el.querySelector('.price__container');
        if (!c) return;
        if (el.querySelector('.bsscommerce-ltsp-message')) { PriceBank.cache.set(id, { state: 'hidden' }); return; }
        PriceBank.cache.set(id, { state: 'ok', cents: PriceBank.parse(c) });
      });
    },

    /* BSS 写入的是「原价HTML + 新价HTML」，取最后一个不在 <s>/<del> 内的金额 */
    parse: function (container) {
      var best = null;
      container.querySelectorAll('*').forEach(function (el) {
        if (el.closest('s, del') || el.children.length) return;
        var m = (el.textContent || '').match(/[\d][\d,]*\.?\d{0,2}/g);
        if (m && m.length) best = m[m.length - 1];
      });
      if (!best) {
        var m2 = (container.textContent || '').match(/[\d][\d,]*\.?\d{0,2}/g);
        if (m2 && m2.length) best = m2[m2.length - 1];
      }
      if (!best) return null;
      var n = parseFloat(best.replace(/,/g, ''));
      return isNaN(n) ? null : Math.round(n * 100);
    },

    mount: function (variantId) {
      var rec = Catalog.byVariant.get(String(variantId));
      if (!rec || !bank) return;
      if (bank.querySelector('[data-variant-id="' + variantId + '"]')) return;
      var el = document.createElement('div');
      el.className = 'mo-qo-anchor price';
      el.setAttribute('data-mo-qo-anchor', '');
      el.setAttribute('data-variant-id', String(variantId));
      el.innerHTML =
        '<div class="price__container" bss-b2b-product-id="' + rec.product.id + '" bss-b2b-product-price' +
        ' bss-b2b-variant-price bss-b2b-variant-id="' + variantId + '" bss-b2b-featured-product="true"' +
        ' bss-b2b-product-qb-id="' + rec.product.id + '">' +
        '<div class="price__regular"><span class="price-item price-item--regular"></span></div></div>' +
        '<div class="bss-b2b-qb-table"></div>' +
        '<span bss-b2b-product-trigger-added="true" hidden></span>';
      bank.appendChild(el);
    },

    /* 未登录时直接标 hidden，绝不建带基础价的锚点 */
    ensure: function (ids) {
      if (!cfg.loggedIn) {
        ids.forEach(function (id) { PriceBank.cache.set(String(id), { state: 'hidden' }); });
        return Promise.resolve();
      }
      PriceBank.harvest();
      var missing = ids.map(String).filter(function (id) { return !PriceBank.cache.has(id); });
      if (!missing.length) return Promise.resolve();
      missing.forEach(PriceBank.mount);

      // 阶梯重试，对应 bss-b2b-v3.js 里可触发重扫的几条路径
      var steps = [
        function () { window.dispatchEvent(new Event('bss_b2b:CustomInitProductSubject')); },
        function () { window.dispatchEvent(new Event('scroll')); },
        function () {},
      ];
      var i = 0;
      return new Promise(function (resolve) {
        (function attempt() {
          if (i >= steps.length) {
            missing.forEach(function (id) { if (!PriceBank.cache.has(id)) PriceBank.cache.set(id, { state: 'unresolved' }); });
            return resolve();
          }
          try { steps[i++](); } catch (e) {}
          setTimeout(function () {
            PriceBank.harvest();
            if (!missing.filter(function (id) { return !PriceBank.cache.has(id); }).length) return resolve();
            attempt();
          }, 1100);
        })();
      });
    },

    get: function (id) { return PriceBank.cache.get(String(id)) || { state: 'unresolved' }; },

    /* 商品级「From $x」：取已解析变体里的最低价 */
    from: function (product) {
      var cents = null, hidden = false;
      product.variants.forEach(function (v) {
        var r = PriceBank.get(v.id);
        if (r.state === 'hidden') hidden = true;
        if (r.state === 'ok' && r.cents != null) cents = cents == null ? r.cents : Math.min(cents, r.cents);
      });
      return { cents: cents, hidden: hidden };
    },
  };

  function priceHTML(product) {
    if (!cfg.loggedIn) return '<a href="' + loginHref() + '">Login to see price</a>';
    var r = PriceBank.from(product);
    if (r.cents == null) return '<span class="mo-qo__pricepending">—</span>';
    return (product.variants.length > 1 ? '<small>From</small>' : '') + esc(money(r.cents));
  }

  /* ───────── 筛选 / 排序 / 渲染列表 ───────── */
  function matches(p) {
    if (view.brand && p.brand !== view.brand) return false;
    if (view.collection) {
      var set = collectionIndex.get(view.collection);
      if (set && !set.has(p.id)) return false;
    }
    var q = up(view.q);
    if (!q) return true;
    if (up(p.title).indexOf(q) >= 0) return true;
    return p.variants.some(function (v) { return up(v.sku).indexOf(q) >= 0 || up(v.colour).indexOf(q) >= 0; });
  }

  function applyFilters() {
    var list = Catalog.list.filter(matches);
    var s = view.sort;
    list.sort(function (a, b) {
      if (s === 'title-desc') return b.title.localeCompare(a.title);
      if (s === 'colors-desc') return b.variants.length - a.variants.length || a.title.localeCompare(b.title);
      return a.title.localeCompare(b.title);
    });
    view.filtered = list;
    view.shown = 0;
    renderList(true);
  }

  /* 数量步进器：−/+ 包住输入框。attr 是行内/弹窗各自的 data 钩子名，
     两处共用同一套结构，事件在 bind() 里按 [data-mo-qo-step] 统一委托处理。 */
  function stepperHTML(attr, id, qty, label) {
    return '<div class="mo-qo__stepper">' +
      '<button type="button" class="mo-qo__step" data-mo-qo-step="-1" tabindex="-1"' +
        ' aria-label="Decrease quantity' + (label ? ' for ' + esc(label) : '') + '">&minus;</button>' +
      '<input type="number" class="mo-qo__qty' + (qty > 0 ? ' mo-qo__qty--filled' : '') + '"' +
        ' min="0" step="1" value="' + qty + '" inputmode="numeric"' +
        ' aria-label="Quantity' + (label ? ' for ' + esc(label) : '') + '" ' + attr + '="' + id + '">' +
      '<button type="button" class="mo-qo__step" data-mo-qo-step="1" tabindex="-1"' +
        ' aria-label="Increase quantity' + (label ? ' for ' + esc(label) : '') + '">+</button>' +
    '</div>';
  }

  /* 选项数量文案：直接给 optionName 加 s 会得到 "quantitys"（清洁布的选项名是 Quantity） */
  function optLabel(p) {
    var n = (p.optionName || '').toLowerCase();
    var c = p.variants.length;
    if (/colou?r/.test(n)) return c + ' colors available';
    return c + ' options available';
  }

  function rowHTML(p) {
    var anyAvail = p.variants.some(function (v) { return v.available; });
    var multi = p.variants.length > 1;
    var act = multi
      ? '<button type="button" class="mo-qo__btn mo-qo__btn--navy" data-mo-qo-open="' + p.id + '">' +
        'View options (' + p.variants.length + ')</button>'
      : !cfg.loggedIn
      ? '<a class="mo-qo__btn mo-qo__btn--navy" href="' + loginHref() + '">Log in to order</a>'
      : stepperHTML('data-mo-qo-rowqty', p.variants[0].id, 0, p.title) +
        '<button type="button" class="mo-qo__btn mo-qo__btn--gold" data-mo-qo-rowadd="' + p.variants[0].id + '">Add to cart</button>';
    return '<div class="mo-qo__row" data-pid="' + p.id + '">' +
      '<span class="mo-qo__thumb" data-zoom-label="' + esc(p.title) + '">' +
        (p.image ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="144" height="104">' : '') +
      '</span>' +
      '<div class="mo-qo__main">' +
        '<div>' +
          '<p class="mo-qo__title">' + esc(p.title) + '</p>' +
          (multi ? '<p class="mo-qo__sub">' + esc(optLabel(p)) + '</p>' : '') +
          '<span class="mo-qo__stock' + (anyAvail ? '' : ' mo-qo__stock--out') + '"><i></i>' +
            (anyAvail ? 'In Stock' : 'Out of Stock') + '</span>' +
        '</div>' +
        '<div class="mo-qo__price" data-mo-qo-price="' + p.id + '">' + priceHTML(p) + '</div>' +
        '<div class="mo-qo__rowact">' + act + '</div>' +
      '</div>' +
    '</div>';
  }

  function renderList(reset) {
    var host = $('[data-mo-qo-list]');
    if (reset) host.innerHTML = '';
    if (!view.filtered.length) {
      host.innerHTML = '<div class="mo-qo__empty">No products match your filters.</div>';
      $('[data-mo-qo-more]').hidden = true;
      renderCount();
      return;
    }
    var next = view.filtered.slice(view.shown, view.shown + cfg.pageSize);
    host.insertAdjacentHTML('beforeend', next.map(rowHTML).join(''));
    view.shown += next.length;
    $('[data-mo-qo-more]').hidden = view.shown >= view.filtered.length;
    renderCount();
    // 只为刚渲染出来的行取价（每商品首个变体足够算 From）
    PriceBank.ensure(next.map(function (p) { return p.variants[0].id; })).then(function () {
      next.forEach(function (p) {
        var cell = $('[data-mo-qo-price="' + p.id + '"]');
        if (cell) cell.innerHTML = priceHTML(p);
      });
    });
  }

  function renderCount() {
    $('[data-mo-qo-count]').innerHTML = view.filtered.length === Catalog.list.length
      ? 'Showing <strong>' + view.shown + '</strong> of <strong>' + Catalog.list.length + '</strong> products'
      : '<strong>' + view.filtered.length + '</strong> product' + (view.filtered.length === 1 ? '' : 's') + ' match';
  }

  /* ───────── 弹窗 ───────── */
  function openModal(pid) {
    var p = Catalog.list.filter(function (x) { return String(x.id) === String(pid); })[0];
    if (!p) return;
    modal.product = p;
    modal.qty = new Map();
    $('[data-mo-qo-modaltitle]').textContent = p.title;
    renderModalBody();
    $('[data-mo-qo-modal]').hidden = false;
    document.body.style.overflow = 'hidden';
    var first = $('[data-mo-qo-vqty]');
    if (first) first.focus();
    PriceBank.ensure(p.variants.map(function (v) { return v.id; })).then(renderModalBody);
  }

  function closeModal() {
    $('[data-mo-qo-modal]').hidden = true;
    document.body.style.overflow = '';
    modal.product = null;
    modal.qty = new Map();
  }

  function renderModalBody() {
    var p = modal.product;
    if (!p) return;
    $('[data-mo-qo-modal]').classList.toggle('mo-qo__modal--guest', !cfg.loggedIn);
    $('[data-mo-qo-modalbody]').innerHTML = p.variants.map(function (v) {
      var r = PriceBank.get(v.id);
      var qty = modal.qty.get(String(v.id)) || 0;
      var unit = !cfg.loggedIn ? '<a href="' + loginHref() + '">Login</a>'
        : (r.state === 'ok' && r.cents != null ? esc(money(r.cents)) : '—');
      var line = (cfg.loggedIn && r.state === 'ok' && r.cents != null) ? esc(money(r.cents * qty)) : '—';
      return '<div class="mo-qo__vrow">' +
        '<span class="mo-qo__vthumb">' + (v.image ? '<img src="' + esc(v.image) + '" alt="" loading="lazy" width="112" height="80">' : '') + '</span>' +
        '<div class="mo-qo__vmain">' +
          '<div>' +
            '<p class="mo-qo__vname">' + esc(p.title) + ' — ' + esc(v.colour) + '</p>' +
            (v.sku ? '<p class="mo-qo__vsku">SKU ' + esc(v.sku) + '</p>' : '') +
            '<span class="mo-qo__stock' + (v.available ? '' : ' mo-qo__stock--out') + '"><i></i>' +
              (v.available ? 'In Stock' : 'Out of Stock') + '</span>' +
          '</div>' +
          (cfg.loggedIn
            ? stepperHTML('data-mo-qo-vqty', v.id, qty, v.colour) +
              '<span class="mo-qo__vprice">' + unit + '</span>' +
              '<span class="mo-qo__vtotal">' + line + '</span>'
            : '') +
        '</div>' +
      '</div>';
    }).join('');
    renderModalFoot();
  }

  function renderModalFoot() {
    var p = modal.product;
    if (!cfg.loggedIn) {
      $('[data-mo-qo-modalsubtotal]').textContent = '—';
      $('[data-mo-qo-modaladd]').disabled = true;
      $('[data-mo-qo-modalclear]').disabled = true;
      return;
    }
    var units = 0, cents = 0, known = true;
    if (p) {
      p.variants.forEach(function (v) {
        var q = modal.qty.get(String(v.id)) || 0;
        if (!q) return;
        units += q;
        var r = PriceBank.get(v.id);
        if (r.state === 'ok' && r.cents != null) cents += r.cents * q; else known = false;
      });
    }
    $('[data-mo-qo-modalsubtotal]').textContent = !cfg.loggedIn
      ? '—' : (units === 0 ? money(0) : (known ? money(cents) : money(cents) + '*'));
    $('[data-mo-qo-modaladd]').disabled = units === 0 || busy;
    $('[data-mo-qo-modalclear]').disabled = units === 0;
  }

  /* ───────── 加入购物车 ───────── */
  function setAlert(msg, kind) {
    var el = $('[data-mo-qo-alert]');
    el.className = 'mo-qo__alert' + (kind ? ' mo-qo__alert--' + kind : '');
    el.innerHTML = msg || '';
    el.hidden = !msg;
    if (msg) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  function addToCart(items, label) {
    if (!items.length || busy) return Promise.resolve(false);
    busy = true;
    setAlert('');
    return fetch(cfg.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
      .then(function (r) {
        if (!r.ok) {
          // items[] 是原子的：一条失败即整批未入车
          var desc = (r.data && (r.data.description || r.data.message)) || 'Could not add these items.';
          setAlert('<strong>Nothing was added.</strong> ' + esc(desc), 'err');
          return false;
        }
        setAlert(esc(label) + ' added to your cart. <a href="' + cfg.cartUrl + '">View cart →</a>', 'ok');
        return true;
      })
      .catch(function () { setAlert('Network error — please try again.', 'err'); return false; })
      .finally(function () { busy = false; refreshCart(); });
  }

  function refreshCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var label = cart.item_count
          ? cart.item_count + ' unit' + (cart.item_count === 1 ? '' : 's') + ' · ' + cart.items.length +
            ' line' + (cart.items.length === 1 ? '' : 's') + (cfg.loggedIn ? ' · ' + money(cart.total_price) : '')
          : 'Cart is empty';
        $('[data-mo-qo-cartlabel]').textContent = label;
        var bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
        if (bubble) bubble.textContent = cart.item_count;
      })
      .catch(function () {});
  }

  /* ───────── 事件 ───────── */
  function debounce(fn, ms) { var t; return function () { clearTimeout(t); t = setTimeout(fn, ms); }; }

  function bind() {
    $('[data-mo-qo-search]').addEventListener('input', debounce(function () {
      view.q = $('[data-mo-qo-search]').value; applyFilters();
    }, 220));
    $('[data-mo-qo-brand]').addEventListener('change', function () { view.brand = this.value; applyFilters(); });
    $('[data-mo-qo-collection]').addEventListener('change', function () {
      view.collection = this.value;
      ensureCollection(view.collection).then(applyFilters);
    });
    $('[data-mo-qo-sort]').addEventListener('change', function () { view.sort = this.value; applyFilters(); });
    $('[data-mo-qo-more]').addEventListener('click', function () { renderList(false); });

    // 列表：打开弹窗 / 单变体行内加购（事件委托，适用于「加载更多」后的新行）
    $('[data-mo-qo-list]').addEventListener('click', function (e) {
      if (onStep(e)) return;
      var open = e.target.closest('[data-mo-qo-open]');
      if (open) return openModal(open.getAttribute('data-mo-qo-open'));
      var add = e.target.closest('[data-mo-qo-rowadd]');
      if (!add) return;
      var vid = add.getAttribute('data-mo-qo-rowadd');
      var input = $('[data-mo-qo-rowqty="' + vid + '"]');
      var qty = Math.max(0, parseInt(input && input.value, 10) || 0);
      if (!qty) { setAlert('Enter a quantity first.', 'err'); if (input) input.focus(); return; }
      var rec = Catalog.byVariant.get(String(vid));
      add.classList.add('is-busy'); add.disabled = true;
      addToCart([{ id: Number(vid), quantity: qty }], qty + ' × ' + (rec ? rec.product.title : 'item'))
        .then(function (okRes) { if (okRes && input) { input.value = 0; input.classList.remove('mo-qo__qty--filled'); } })
        .finally(function () { add.classList.remove('is-busy'); add.disabled = false; });
    });
    /* −/+ 只改输入框的值，再派发 input 让既有逻辑接管 —— 不重复一份小计计算 */
    function onStep(e) {
      var b = e.target.closest('[data-mo-qo-step]');
      if (!b) return false;
      var box = b.closest('.mo-qo__stepper');
      var input = box && box.querySelector('input');
      if (!input) return true;
      var next = (parseInt(input.value, 10) || 0) + Number(b.getAttribute('data-mo-qo-step'));
      input.value = Math.max(0, next);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return true;
    }

    $('[data-mo-qo-list]').addEventListener('input', function (e) {
      var i = e.target.closest('[data-mo-qo-rowqty]');
      if (i) i.classList.toggle('mo-qo__qty--filled', (parseInt(i.value, 10) || 0) > 0);
    });

    // 弹窗
    var m = $('[data-mo-qo-modal]');
    m.addEventListener('click', function (e) {
      if (onStep(e)) return;
      if (e.target.closest('[data-mo-qo-modalclose]')) return closeModal();
    });
    m.addEventListener('input', function (e) {
      var i = e.target.closest('[data-mo-qo-vqty]');
      if (!i) return;
      var q = Math.max(0, parseInt(i.value, 10) || 0);
      modal.qty.set(i.getAttribute('data-mo-qo-vqty'), q);
      i.classList.toggle('mo-qo__qty--filled', q > 0);
      // 只更新小计与页脚，不重渲染整个 body（否则输入焦点会丢）
      var row = i.closest('.mo-qo__vrow');
      var r = PriceBank.get(i.getAttribute('data-mo-qo-vqty'));
      var tot = row && row.querySelector('.mo-qo__vtotal');
      if (tot) tot.textContent = (cfg.loggedIn && r.state === 'ok' && r.cents != null) ? money(r.cents * q) : '—';
      renderModalFoot();
    });
    $('[data-mo-qo-modalclear]').addEventListener('click', function () {
      modal.qty = new Map(); renderModalBody();
    });
    $('[data-mo-qo-modaladd]').addEventListener('click', function () {
      var p = modal.product; if (!p) return;
      var items = [], units = 0;
      p.variants.forEach(function (v) {
        var q = modal.qty.get(String(v.id)) || 0;
        if (q > 0) { items.push({ id: Number(v.id), quantity: q }); units += q; }
      });
      if (!items.length) return;
      var btn = this;
      btn.classList.add('is-busy'); btn.disabled = true;
      addToCart(items, units + ' × ' + p.title)
        .then(function (okRes) { if (okRes) closeModal(); })
        .finally(function () { btn.classList.remove('is-busy'); renderModalFoot(); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !$('[data-mo-qo-modal]').hidden) closeModal();
    });
  }

  function fillSelects() {
    var b = $('[data-mo-qo-brand]');
    Catalog.brands.forEach(function (x) {
      var o = document.createElement('option'); o.value = x; o.textContent = x; b.appendChild(o);
    });
    var c = $('[data-mo-qo-collection]');
    Catalog.collections.forEach(function (x) {
      var o = document.createElement('option'); o.value = x.handle; o.textContent = x.title; c.appendChild(o);
    });
  }

  /* ───────── 启动 ───────── */
  function init() {
    root = document.querySelector('[data-mo-qo]');
    if (!root) return;
    try { cfg = JSON.parse($('[data-mo-qo-config]').textContent); } catch (e) { cfg = {}; }
    cfg.pageSize = cfg.pageSize || 24;
    bank = $('[data-mo-qo-bank]');

    bind();
    PriceBank.harvest();
    refreshCart();

    Promise.all([loadCatalog(), loadCollections()]).then(function () {
      fillSelects();
      applyFilters();
      root.setAttribute('data-mo-qo-ready', '1');
    }).catch(function () {
      $('[data-mo-qo-list]').innerHTML = '<div class="mo-qo__empty">Could not load the catalog. Please refresh the page.</div>';
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
